import React, { Suspense } from 'react';
import { CustomTable } from '../components/CustomTable';
import { groupsTableHeaders } from '../utils/Enums/TableHeaders';
import { PaginationComponent } from '../components/Pagination';
import { Group, TableComponentProps, TableInnerProps } from '../models/table-models';
import { useTableData } from '../utils/data-hooks';
import { resourceCache } from 'use-async-resource';
import { Spinner } from 'react-bootstrap';
import { useDeleteGroups, useGetGroups } from '../utils/groups-hooks';
import { AddActionComponent } from '../components/AddActionComponent';
import { AddEditGroup } from './AddEditGroup';
import { useTranslation } from 'react-i18next';
import { NoDataComponent } from '../components/NoDataComponent';
import {toast} from "react-toastify";
import {CustomToastContent} from "../components/CustomToastContent";
import {toastOptionsTopRight} from "../utils/Enums/ToastOptions";

export const Groups = ({ filter, pagination }: TableComponentProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });

    const [getGroups, fetchNewGroups] = useTableData<Group[]>(useGetGroups, pagination, filter);

    const refetchEntitiesHook = () => {
        resourceCache(useGetGroups).clear();
        fetchNewGroups({
            'pager.page': pagination.currentPage,
            'pager.itemsPerPage': pagination.pageSize,
            pagination,
            filter,
        });
    };

    return (
        <>
            <div className='d-flex w-100 justify-content-between mb-3'>
                <AddActionComponent
                    accessActionRoles={[]}
                    addComponent={AddEditGroup}
                    title={t('addGroup')}
                    {...{ refetchEntitiesHook }}
                ></AddActionComponent>
            </div>
            <Suspense fallback={<Spinner animation='border' />}>
                <GroupsInner pagination={pagination} getEntities={getGroups} {...{ refetchEntitiesHook }} />
            </Suspense>
        </>
    );
};

const GroupsInner = ({ getEntities, pagination, refetchEntitiesHook }: TableInnerProps<Group>) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { pager, items } = getEntities();

    const useDelete = async (group: Group) => {
        await useDeleteGroups(group);
        refetchEntitiesHook();
        toast.success(<CustomToastContent content={[t('groupDeleted')]}/>, toastOptionsTopRight);
    }

    return (
        <>
            {pager.totalCount > 0 ? (
                <>
                    <CustomTable
                        columns={{
                            headings: groupsTableHeaders,
                            items: items.map(({ mainGroup, subGroup, timezone, language }) => [
                                <p style={{ color: 'red' }}>{mainGroup}</p>,
                                subGroup,
                                timezone,
                                language,
                            ]),
                        }}
                        accessActionRoles={[]}
                        accessEditRoles={[]}
                        actionHook={useDelete}
                        tableData={items}
                        {...{ refetchEntitiesHook }}
                    ></CustomTable>
                    <PaginationComponent recordsCount={pager.totalCount} pagination={pagination} />
                </>
            ) : (
                <NoDataComponent noDataText={t('groups')}></NoDataComponent>
            )}
        </>
    );
};
